import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { InvoiceDocumentType } from '../../enums/invoiceDocumentType'
import { useInvoiceComplianceMode } from '../useInvoiceComplianceMode'
import { useSendAsEInvoiceMutation } from './useSendAsEInvoiceMutation'
import { useSendAsEInvoiceViaFrenchPdpMutation } from './useSendAsEInvoiceViaFrenchPdpMutation'

interface UseSendAsEInvoiceOptions {
  onError?: (invoiceId: number) => void
  onSuccess?: (invoiceId: number) => void
}

interface SendAsEInvoiceOptions {
  addressingLine?: string
  attachmentUuids?: string[]
  companyName: string
  documentType: InvoiceDocumentType
  invoiceId: number
  isPublicEntity: boolean
}

export const useSendAsEInvoice = ({ onError, onSuccess }: UseSendAsEInvoiceOptions = {}) => {
  const { t } = useTranslation()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { send: sendStandard, isProcessing: isProcessingSendStandard } = useSendAsEInvoiceMutation()
  const { send: sendViaFrenchPdp, isProcessing: isProcessingSendViaFrenchPdp } = useSendAsEInvoiceViaFrenchPdpMutation()

  const sendAsEInvoice = useCallback(
    ({
      addressingLine,
      attachmentUuids,
      companyName,
      documentType,
      invoiceId,
      isPublicEntity,
    }: SendAsEInvoiceOptions) => {
      const mutationOptions = {
        onSuccess: () => {
          notify({
            id: NotificationKeys.ExternalInvoiceSentAsEInvoice,
            message: t('external_invoices.send_as_e_invoice.notification.success', { companyName }),
            variant: 'success',
          })
          onSuccess?.(invoiceId)
        },
        onError: () => {
          notify({
            id: NotificationKeys.ExternalInvoiceSentAsEInvoice,
            message: t('external_invoices.send_as_e_invoice.notification.error'),
            variant: 'error',
          })
          onError?.(invoiceId)
        },
      }

      if (isFrenchComplianceMode) {
        sendViaFrenchPdp({ documentType, invoiceId, payload: { addressingLine } }, mutationOptions)

        return
      }

      sendStandard({ documentType, invoiceId, payload: { attachmentUuids, isPublicEntity } }, mutationOptions)
    },
    [isFrenchComplianceMode, onError, onSuccess, sendStandard, sendViaFrenchPdp, t],
  )

  return {
    isProcessing: isFrenchComplianceMode ? isProcessingSendViaFrenchPdp : isProcessingSendStandard,
    sendAsEInvoice,
  }
}
